import React, { useState, useEffect } from 'react';

const BudgetOperationForm = ({ operation, onSave, onCancel, currentUser }) => {
  const [formData, setFormData] = useState({
    link_cuenta: '',
    fecha_peticion: '',
    celula: '',
    nombre: '',
    campana: '',
    acciones: '',
    presupuesto_actual: '',
    subir_ppto_diario_a: '',
    mes: '',
    observaciones: '',
    seguimiento: '',
    status: 'pendiente',
    fechas_aumento: ['', '', '', '', ''] // Inicializar con 5 fechas vacías por defecto (mínimo requerido)
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // Opciones predefinidas
  const meses = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const celulas = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4',
    'G7M1', 'G7M2', 'G7M3', 'G7M4'
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente' },
    { value: 'en_proceso', label: '🔄 En Proceso' },
    { value: 'completado', label: '✅ Completado' },
    { value: 'cancelado', label: '❌ Cancelado' }
  ];

  useEffect(() => {
    loadUsers();
    loadClients();
    if (operation) {
      // Procesar fechas_aumento desde la operación existente
      let fechasAumento = [];
      if (operation.fechas_aumento) {
        // Si es un string JSON, parsearlo
        if (typeof operation.fechas_aumento === 'string') {
          try {
            fechasAumento = JSON.parse(operation.fechas_aumento);
          } catch (e) {
            console.error('Error al parsear fechas_aumento:', e);
            fechasAumento = [operation.fechas_aumento];
          }
        } else if (Array.isArray(operation.fechas_aumento)) {
          fechasAumento = operation.fechas_aumento;
        }
      }
      
      // Asegurar que haya al menos 5 campos de fecha (completar con fechas vacías si es necesario)
      // Esto garantiza que siempre tengamos el mínimo requerido de fechas disponibles
      while (fechasAumento.length < 5) {
        fechasAumento.push('');
      }
      
      setFormData({
        link_cuenta: operation.link_cuenta || '',
        fecha_peticion: operation.fecha_peticion ? operation.fecha_peticion.split('T')[0] : '',
        celula: operation.celula || '',
        nombre: operation.nombre || '',
        campana: operation.campana || '',
        acciones: operation.acciones || '',
        presupuesto_actual: operation.presupuesto_actual || '',
        subir_ppto_diario_a: operation.subir_ppto_diario_a || '',
        mes: operation.mes || '',
        observaciones: operation.observaciones || '',
        seguimiento: operation.seguimiento || '',
        status: operation.status || 'pendiente',
        fechas_aumento: fechasAumento
      });
    } else {
      // Para nuevos registros, establecer el mes actual y el usuario actual como seguimiento
      const currentMonth = new Date().toLocaleDateString('es-ES', { month: 'long' });
      const capitalizedMonth = currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1);
      setFormData(prev => ({
        ...prev,
        mes: capitalizedMonth,
        fecha_peticion: new Date().toISOString().split('T')[0],
        seguimiento: currentUser?.name || ''
      }));
    }
  }, [operation, currentUser]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/users`);
      if (response.ok) {
        const data = await response.json();
        console.log('Usuarios cargados desde API:', data);
        setUsers(data);
      } else {
        console.error('Error al cargar usuarios:', response.status);
        setUsers([]);
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/clients`);
      if (response.ok) {
        const data = await response.json();
        console.log('Clientes cargados desde API:', data);
        setClients(data);
      } else {
        console.error('Error al cargar clientes:', response.status);
        setClients([]);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleClientNameChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      nombre: value
    }));

    // Filtrar clientes basado en el texto ingresado
    if (value.length > 0) {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowClientSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowClientSuggestions(false);
    }

    // Limpiar error del campo
    if (errors.nombre) {
      setErrors(prev => ({
        ...prev,
        nombre: ''
      }));
    }
  };

  const selectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      nombre: client.name
    }));
    setShowClientSuggestions(false);
    setFilteredClients([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.link_cuenta.trim()) newErrors.link_cuenta = 'El link de la cuenta es requerido';
    if (!formData.fecha_peticion) newErrors.fecha_peticion = 'La fecha de petición es requerida';
    if (!formData.celula.trim()) newErrors.celula = 'La célula es requerida';
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre del cliente es requerido';
    if (!formData.campana.trim()) newErrors.campana = 'La campaña es requerida';
    if (!formData.mes.trim()) newErrors.mes = 'El mes es requerido';
    
    // Validar que al menos una fecha de aumento tenga valor
    const fechasValidas = formData.fechas_aumento.filter(fecha => fecha && fecha.trim());
    if (fechasValidas.length === 0) {
      newErrors.fechas_aumento = 'Se requiere al menos una fecha de aumento';
    }
    
    if (formData.subir_ppto_diario_a && isNaN(parseFloat(formData.subir_ppto_diario_a))) {
      newErrors.subir_ppto_diario_a = 'El presupuesto debe ser un número válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Filtrar fechas vacías antes de enviar
    const processedFormData = {
      ...formData,
      fechas_aumento: formData.fechas_aumento.filter(fecha => fecha && fecha.trim())
    };

    setLoading(true);
    try {
      await onSave(processedFormData);
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                {operation ? '✏️ Editar Operación' : '➕ Nueva Operación'}
              </h2>
              <p className="text-blue-100 mt-1">
                Aumento de presupuesto Google
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Primera fila */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mes *
                </label>
                <select
                  name="mes"
                  value={formData.mes}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.mes ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar mes</option>
                  {meses.map(mes => (
                    <option key={mes} value={mes}>{mes}</option>
                  ))}
                </select>
                {errors.mes && <p className="text-red-500 text-sm mt-1">{errors.mes}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Agente de Seguimiento
                </label>
                {loadingUsers ? (
                  <div className="w-full p-3 border-2 border-gray-300 rounded-xl bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-gray-500 text-sm">Cargando agentes...</span>
                  </div>
                ) : (
                  <select
                    name="seguimiento"
                    value={formData.seguimiento}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Seleccionar agente</option>
                    {users.map(user => {
                      const fullName = user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : (user.firstName || user.lastName || user.email);
                      
                      return (
                        <option key={user.email || user.id} value={fullName}>
                          {fullName} ({user.role || 'usuario'})
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Petición *
                </label>
                <input
                  type="date"
                  name="fecha_peticion"
                  value={formData.fecha_peticion}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.fecha_peticion ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.fecha_peticion && <p className="text-red-500 text-sm mt-1">{errors.fecha_peticion}</p>}
              </div>
            </div>

            {/* Segunda fila */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre del Cliente *
                </label>
                {loadingClients ? (
                  <div className="w-full p-3 border-2 border-gray-300 rounded-xl bg-gray-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-gray-500 text-sm">Cargando clientes...</span>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleClientNameChange}
                      onFocus={() => {
                        if (formData.nombre.length > 0) {
                          const filtered = clients.filter(client =>
                            client.name.toLowerCase().includes(formData.nombre.toLowerCase())
                          );
                          setFilteredClients(filtered);
                          setShowClientSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay para permitir click en sugerencias
                        setTimeout(() => setShowClientSuggestions(false), 200);
                      }}
                      placeholder="Escribe el nombre del cliente..."
                      className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                        errors.nombre ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    
                    {/* Sugerencias de clientes */}
                    {showClientSuggestions && filteredClients.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredClients.slice(0, 10).map((client, index) => (
                          <div
                            key={client.id || index}
                            onClick={() => selectClient(client)}
                            className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{client.name}</div>
                            {client.specialty && (
                              <div className="text-sm text-gray-500">{client.specialty}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {errors.nombre && <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Link Cuenta Publicitaria *
                </label>
                <input
                  type="url"
                  name="link_cuenta"
                  value={formData.link_cuenta}
                  onChange={handleChange}
                  placeholder="https://ads.google.com/..."
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.link_cuenta ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.link_cuenta && <p className="text-red-500 text-sm mt-1">{errors.link_cuenta}</p>}
              </div>
            </div>

            {/* Tercera fila */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Célula *
                </label>
                <select
                  name="celula"
                  value={formData.celula}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.celula ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar célula</option>
                  {celulas.map(celula => (
                    <option key={celula} value={celula}>{celula}</option>
                  ))}
                </select>
                {errors.celula && <p className="text-red-500 text-sm mt-1">{errors.celula}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Campaña */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Campaña *
              </label>
              <input
                type="text"
                name="campana"
                value={formData.campana}
                onChange={handleChange}
                placeholder="Nombre de la campaña"
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.campana ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.campana && <p className="text-red-500 text-sm mt-1">{errors.campana}</p>}
            </div>

            {/* Presupuesto */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Presupuesto Actual
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="presupuesto_actual"
                  value={formData.presupuesto_actual}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                <p className="text-xs text-gray-500 mt-1">Presupuesto antes del aumento</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subir Presupuesto Diario A:
                </label>
                <input
                  type="number"
                  step="0.01"
                  name="subir_ppto_diario_a"
                  value={formData.subir_ppto_diario_a}
                  onChange={handleChange}
                  placeholder="0.00"
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.subir_ppto_diario_a ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.subir_ppto_diario_a && <p className="text-red-500 text-sm mt-1">{errors.subir_ppto_diario_a}</p>}
                
                {/* Mostrar comparación si ambos valores están presentes */}
                {formData.presupuesto_actual && formData.subir_ppto_diario_a && (
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Diferencia:</span>
                      <span className={`font-medium ${
                        parseFloat(formData.subir_ppto_diario_a) > parseFloat(formData.presupuesto_actual) 
                          ? 'text-green-600' 
                          : parseFloat(formData.subir_ppto_diario_a) < parseFloat(formData.presupuesto_actual)
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}>
                        ${(parseFloat(formData.subir_ppto_diario_a) - parseFloat(formData.presupuesto_actual)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-gray-600">Porcentaje:</span>
                      <span className={`font-medium ${
                        parseFloat(formData.subir_ppto_diario_a) > parseFloat(formData.presupuesto_actual) 
                          ? 'text-green-600' 
                          : parseFloat(formData.subir_ppto_diario_a) < parseFloat(formData.presupuesto_actual)
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}>
                        {((parseFloat(formData.subir_ppto_diario_a) - parseFloat(formData.presupuesto_actual)) / parseFloat(formData.presupuesto_actual) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fechas de Aumento */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fechas de Aumento *
              </label>
              <div className="space-y-3">
                {formData.fechas_aumento.map((fecha, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-none w-8 text-right text-gray-500 text-sm">
                      {index + 1}.
                    </div>
                    <input
                      type="date"
                      value={fecha}
                      onChange={(e) => {
                        const newFechas = [...formData.fechas_aumento];
                        newFechas[index] = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          fechas_aumento: newFechas
                        }));
                      }}
                      className={`flex-1 p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                        errors.fechas_aumento && !fecha ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {formData.fechas_aumento.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newFechas = formData.fechas_aumento.filter((_, i) => i !== index);
                          setFormData(prev => ({
                            ...prev,
                            fechas_aumento: newFechas
                          }));
                        }}
                        className="p-2 text-red-500 hover:text-red-700 transition-colors"
                        title="Eliminar esta fecha"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {errors.fechas_aumento && (
                  <p className="text-red-500 text-sm mt-1">{errors.fechas_aumento}</p>
                )}
                <div className="flex justify-between items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        fechas_aumento: [...prev.fechas_aumento, '']
                      }));
                    }}
                    className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Agregar otra fecha de aumento
                  </button>
                  <span className="text-xs text-gray-500">
                    {formData.fechas_aumento.length} fecha{formData.fechas_aumento.length !== 1 ? 's' : ''} configurada{formData.fechas_aumento.length !== 1 ? 's' : ''}
                    {formData.fechas_aumento.length < 5 && (
                      <span className="text-amber-600 ml-1">(mínimo recomendado: 5)</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Acciones */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Acciones Tomadas
              </label>
              <textarea
                name="acciones"
                value={formData.acciones}
                onChange={handleChange}
                placeholder="Describe las acciones realizadas o por realizar..."
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Observaciones */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleChange}
                placeholder="Observaciones adicionales..."
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Guardando...
              </div>
            ) : (
              operation ? 'Actualizar Operación' : 'Crear Operación'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetOperationForm;
